import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { HerdCreateRequest } from './types';
import { Herd, herdSchema } from 'app/agriculture/queries/herds/types/Herd';
import { useSelectedFarm } from 'app/agriculture/hooks/useSelectedFarm/useSelectedFarm';
import { MutationOptions } from '../types/MutationOptions';
import { createAxiosInstance } from 'app/agriculture/queries/createAxiosInstance';
import { useEntityCacheInvalidation } from 'app/agriculture/mutations/useEntityCacheInvalidation';
import { useSnapshotCacheInvalidation } from 'app/agriculture/mutations/useSnapshotCacheInvalidation';
import { handleZodParseError } from 'app/agriculture/utils';
import { HerdSnapshot, herdSnapshotSchema } from 'app/agriculture/queries/herds/types/HerdSnapshot';
import { convertDatePropertiesToTZDate } from 'app/agriculture/queries/utils/convertDatePropertiesToTZDate';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';

export function useHerdCreateMutation({
    onSuccess = () => {},
    onError = () => {},
}: MutationOptions = {}) {
    const { timeZone } = useTimeZone();
    const instance = createAxiosInstance();
    const { id: farmId } = useSelectedFarm();
    const { addToCache: addHerdToCache } = useEntityCacheInvalidation('Herd');
    const { addSnapshotToCache, invalidateLatestBefore } = useSnapshotCacheInvalidation('Herd');
    const { mutate, isPending } = useMutation({
        mutationFn: async (data: Omit<HerdCreateRequest, 'farmId'>) => {
            const herd = {
                ...data,
                farmId,
            };
            const response = await instance.post<
                HerdCreateRequest & { farmId: number },
                AxiosResponse<Herd>
            >('/herds/', herd);
            try {
                return herdSchema
                    .extend({
                        snapshot: herdSnapshotSchema
                            .transform((data) => convertDatePropertiesToTZDate(data, timeZone))
                            .nullable(),
                    })
                    .transform((data) => convertDatePropertiesToTZDate(data, timeZone))
                    .parse(response.data) as Herd & { snapshot: HerdSnapshot | null };
            } catch (e) {
                handleZodParseError(e);
                throw e;
            }
        },
        onSuccess: async (herd) => {
            onSuccess({ id: herd.id });
            const { snapshot, ...rawHerd } = herd;
            addHerdToCache(rawHerd);
            if (snapshot != null) {
                addSnapshotToCache(snapshot);
                await invalidateLatestBefore(snapshot.timestamp);
            }
        },
        onError,
    });
    return { mutate, isPending };
}
